// The video ad next to the playlist recommendations in a subject area's hero (ad unit `subject_area_header`), in
// the A/B test's variation only. There is no video to target the ad with, so the subject area stands in for it.

import abTests from 'frontend/shared/javascripts/modules/ab_tests'
import getAdServerKeyValues from 'frontend/shared/javascripts/util/ad_server_key_values'
import { loadPlayer } from 'frontend/shared/javascripts/util/load_player'
import { track } from 'frontend/shared/javascripts/tracking'
import { AD_BANNER_CLIP } from 'frontend/shared/javascripts/video_player/ad_banner_clip'
import { getAdsResponse, type AdsResponse } from 'player/javascripts/player/advertising'
import type Player from 'player/javascripts/player'

const AD_UNIT = 'subject_area_header'
const LOAD_TIMEOUT = 2000
// Both observers watch the document rather than the implicit root, which inside a cross-origin frame ignores
// the margin and measures against the top-level viewport.
const LOAD_AHEAD_MARGIN = '100% 0px'
const AD_BUTTON_LABELS = { durationInfo: 'Jetzt mehr erfahren', visitAd: 'Chancen entdecken' }

interface SubjectAreaHeaderAdData {
  adServerOptions: Record<string, unknown>
}

up.compiler(
  '[data-subject-area-header-ad]',
  (element: HTMLElement, { adServerOptions }: SubjectAreaHeaderAdData) => {
    const playerContainer = element.querySelector<HTMLElement>('[data-player--container]')!
    let player: Promise<Player> | null = null
    let destroyPlayer: (() => Promise<void>) | null = null
    let variantActive = false
    let nearViewport = false
    let inView = false
    let loading = false
    let playStarted = false
    let playing = false
    let finished = false
    let destroyed = false

    const loadObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          nearViewport = true
          loadObserver.disconnect()
          startWhenDue()
        }
      },
      { root: document, rootMargin: LOAD_AHEAD_MARGIN },
    )

    const viewObserver = new IntersectionObserver(
      (entries) => {
        inView = entries[entries.length - 1].intersectionRatio >= 0.5
        if (inView) {
          play()
        } else {
          pause()
        }
      },
      { root: document, threshold: 0.5 },
    )

    function startWhenDue(): void {
      if (variantActive && nearViewport && !loading) {
        loading = true
        void start()
      }
    }

    async function start(): Promise<void> {
      const adsResponse = await loadHeaderAd(adServerOptions)
      if (destroyed) {
        return
      }
      if (!adsResponse) {
        // tracking: player.ad_unfilled
        track('player.unfilledAdSlot', {
          keyValues: getAdServerKeyValues(adServerOptions, AD_UNIT),
          playerType: 'banner',
          ad_unit: AD_UNIT,
        })
        return
      }

      const loaded = loadPlayer(playerContainer, {
        showAds: true,
        isVideoAdBanner: true,
        playerType: 'banner',
        adUnit: AD_UNIT,
        adsResponse,
        adButtonLabels: AD_BUTTON_LABELS,
      })
      destroyPlayer = loaded.destroyPlayer
      player = loaded.playerPromise.then((loadedPlayer) => {
        loadedPlayer.loadVideo(AD_BANNER_CLIP, {})
        return loadedPlayer
      })

      element.classList.add('-filled')
      viewObserver.observe(element)
    }

    function play(): void {
      if (!player || playing || finished) {
        return
      }
      const firstStart = !playStarted
      playStarted = true
      playing = true
      void player.then((loadedPlayer) =>
        firstStart ? loadedPlayer.playWhenReady() : loadedPlayer.resume(),
      )
    }

    // `force` for an ad that started by itself, which has not been counted as playing.
    function pause({ force = false }: { force?: boolean } = {}): void {
      if (!player || (!playing && !force)) {
        return
      }
      playing = false
      void player.then((loadedPlayer) => loadedPlayer.pause())
    }

    // An ad that fails to play leaves the space empty, as if the ad server had not sent one.
    function remove(): void {
      element.classList.remove('-filled')
      viewObserver.disconnect()
      void destroyPlayer?.()
      player = null
      playing = false
    }

    const destructors = [
      up.on(playerContainer, 'app:player:adStarted', () => {
        if (!inView) {
          pause({ force: true })
        }
      }),
      up.on(playerContainer, 'app:player:adBannerFinished', () => {
        finished = true
        playing = false
      }),
      up.on(playerContainer, 'app:player:noAdReceived app:player:mediaError', remove),
      // Not before the variation is decided: an ad requested for a visitor of the original would count as an
      // impression nobody saw.
      abTests.onVariant('subject_area_header_ad', (variant) => {
        variantActive = variant === 'b'
        startWhenDue()
      }),
    ]
    loadObserver.observe(element)

    return () => {
      destroyed = true
      loadObserver.disconnect()
      viewObserver.disconnect()
      destructors.forEach((destructor) => destructor())
      void destroyPlayer?.()
    }
  },
)

/**
 * The ad for the header, or null if none arrives within `LOAD_TIMEOUT`.
 */
export async function loadHeaderAd(
  adServerOptions: Record<string, unknown>,
): Promise<AdsResponse | null> {
  const deadline = new Promise<null>((resolve) => setTimeout(() => resolve(null), LOAD_TIMEOUT))
  const keyValues = getAdServerKeyValues(adServerOptions, AD_UNIT) as unknown as Record<
    string,
    string
  >

  try {
    const result = await Promise.race([getAdsResponse(keyValues, '', AD_UNIT), deadline])
    return result?.[0] ?? null
  } catch {
    // Probably an ad blocker
    return null
  }
}
